import type { Project, ProjectSummary } from '../types/project'

/**
 * Persistencia local (MVP): localStorage.
 *
 * Se eligió localStorage para el MVP por su simplicidad y porque el tamaño típico
 * de un proyecto (algunas decenas de KB en JSON) está muy por debajo de su límite
 * habitual (~5 MB por origen). Si en fases posteriores los proyectos crecen de forma
 * significativa (muchos pisos, miles de objetos, miniaturas de imagen, etc.), la
 * recomendación es migrar esta capa a IndexedDB sin cambiar la API pública de este
 * módulo (las funciones ya son asíncronas para facilitar esa migración).
 */

const INDEX_KEY = 'disena3d:index'
const projectKey = (id: string) => `disena3d:project:${id}`

function readIndex(): ProjectSummary[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY)
    return raw ? (JSON.parse(raw) as ProjectSummary[]) : []
  } catch {
    return []
  }
}

function writeIndex(index: ProjectSummary[]) {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index))
}

export async function listProjects(): Promise<ProjectSummary[]> {
  return readIndex().sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
}

export async function saveProject(project: Project): Promise<void> {
  localStorage.setItem(projectKey(project.id), JSON.stringify(project))
  const index = readIndex().filter((p) => p.id !== project.id)
  index.push({
    id: project.id,
    name: project.name,
    propertyType: project.propertyType,
    updatedAt: project.updatedAt,
    floorsCount: project.floors.length,
  })
  writeIndex(index)
}

export async function loadProjectById(id: string): Promise<Project | null> {
  const raw = localStorage.getItem(projectKey(id))
  return raw ? (JSON.parse(raw) as Project) : null
}

export async function deleteProject(id: string): Promise<void> {
  localStorage.removeItem(projectKey(id))
  writeIndex(readIndex().filter((p) => p.id !== id))
}

export function isStorageAvailable(): boolean {
  try {
    const testKey = '__disena3d_test__'
    localStorage.setItem(testKey, '1')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}
